import type { UserPreferencesHydraItem } from "@verbleif/lib/src/composables/Api/Type/UserPreferences/user_preferences.interface";
import { iriToId } from "@verbleif/lib/src/composables/Utils/iri.helpers";
import { watch } from "vue";
import { useAuthStore } from "../../AuthStore/useAuthStore";
import { useLocationStore } from "../../LocationStore/useLocationStore";
import { requestEffectiveUserPreferences } from "./effectiveUserPreferences.api";

let latestRequest = 0;

/**
 * Loads the effective preferences for the current user and selected location into the auth store.
 * The location is read when the call starts, and a response that a newer call overtook is dropped,
 * so a slow answer for an old location cannot overwrite the preferences of the new one.
 * Every call sends its own request, so a call made after a change was stored always reads that change.
 * This is the one place that writes the effective preferences into the auth store: a caller must not write the
 * returned answer itself, since that would skip the check and let an overtaken answer win.
 */
export async function refreshEffectiveUserPreferences(): Promise<UserPreferencesHydraItem | null> {
  const { user, setUserPreferences } = useAuthStore();
  const { selectedLocation } = useLocationStore();
  const currentUser = user.value;
  if (currentUser === null) {
    return null;
  }

  const locationIri = selectedLocation.value;
  const request = ++latestRequest;
  const data = await requestEffectiveUserPreferences(currentUser.id, locationIri ? iriToId(locationIri) : null);
  if (request === latestRequest) {
    setUserPreferences(data);
  }
  return data;
}

/**
 * Keeps the effective preferences in step with the selected location. Preferences can differ per
 * location (role scoped ones), and screens read them from the auth store, so the refetch must not
 * depend on a preference screen being mounted. Call once from the app root.
 */
export function useSyncEffectiveUserPreferencesWithLocation(): void {
  const { user } = useAuthStore();
  const { selectedLocation } = useLocationStore();

  watch(selectedLocation, () => {
    if (user.value === null) {
      return;
    }
    refreshEffectiveUserPreferences().catch(error => console.error(error));
  });
}
